import React from 'react';
import { styled } from 'sensortower-components/src/utils/styled';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';
import { sidekick } from '../../utils/sidekick';
import Container from '@mui/material/Container';
import { useLocalizationContext } from '../LocalizationContext';

export interface ModuleIntegrationIFrameProps {
  id?: string;
  sidekickLookup: any;
  settings?: {
    src?: string;
    width?: number;
    height?: number;
    aspectRatio?: number;
    padding?: string | number;
  };
}

const ModuleIntegrationIFrame = ({ id, sidekickLookup, settings }: ModuleIntegrationIFrameProps) => {
  const localization = useLocalizationContext();

  if (!settings || !settings.src) return null;

  // These come from the CMS, so technically they can be anything, not only numbers :/
  const width = settings.width ?? '100%';
  const height = settings.height ?? '100%';
  // Ensure that the aspect ratio is a positive number not larger than 1
  const aspectRatio = Math.min(1, Number.isFinite(settings.aspectRatio) ? Math.abs(settings.aspectRatio as number) : 1);
  const title = localization['moduleIntegration.fallbackTitle']?.shortTextValue ?? 'Embedded Content';

  return (
    <ErrorBoundary>
      <StyledContainer padding={settings?.padding} maxWidth="xl">
        <Wrapper width={width} height={height} aspectRatio={aspectRatio}>
          <Root
            id={id}
            title={title}
            width="100%"
            height="100%"
            {...sidekick(sidekickLookup)}
            src={settings?.src}
            allowFullScreen
          />
        </Wrapper>
      </StyledContainer>
    </ErrorBoundary>
  );
};

const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => !['padding'].includes(prop as string),
})((props: { padding?: string | number }) => ({
  padding: props.padding != null ? `${props.padding} !important` : 'inherit',
}));

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => !['width', 'height', 'aspectRatio'].includes(prop as string),
})((props: { width: string | number; height: string | number; aspectRatio: number }) => ({
  position: 'relative',
  width: props.width,
  height: props.height,
  paddingBottom: `calc(${props.aspectRatio} * 100%)`,
  overflow: 'hidden',
}));

const Root = styled('iframe', {
  name: 'ModuleIntegrationIFrame',
  slot: 'EmbedRoot',
  shouldForwardProp: (prop) => !['variant', 'width', 'height'].includes(prop as string),
  overridesResolver: (_, styles) => [styles.root],
})<{ variant?: string }>(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  border: 'none',
}));

export default ModuleIntegrationIFrame;
